// Presentation layer for the employment-verification verdict.
//
// The backend decides the verdict (services/identityMatch.js owns deriveEmploymentStatus);
// this file only decides how it reads in the UI and the CSV. Keeping the split that way
// means the wording can change without touching verification logic.
//
// Why three states instead of the old boolean: `confirmed: false` conflated "this person
// moved" with "we could not read their profile at all", and the export printed both as
// "No - Changed Company". A Microsoft contact whose LinkedIn returned empty employment
// was reported as having left Microsoft — a job change nobody observed.

export const EMPLOYMENT_STATUS = {
  VERIFIED:     'verified',
  UNVERIFIABLE: 'unverifiable',
  REJECTED:     'rejected',
};

// CSV / UI label for the "Employment Verified" column.
//
// `empResult` is what the backend returned for this contact, or null when no check ran.
// `hasLinkedinUrl` separates "never checked" from "no profile to check", which the user
// needs in order to know whether re-running would tell them anything.
export function employmentLabel(empResult, hasLinkedinUrl) {
  if (empResult == null) {
    return hasLinkedinUrl ? 'Not Checked' : 'No LinkedIn';
  }

  // Fall back to the legacy boolean for cached results written before the enum existed.
  const status = empResult.status
    || (empResult.confirmed ? EMPLOYMENT_STATUS.VERIFIED : EMPLOYMENT_STATUS.UNVERIFIABLE);

  switch (status) {
    case EMPLOYMENT_STATUS.VERIFIED:
      return 'Yes - Verified';
    case EMPLOYMENT_STATUS.REJECTED:
      // Name the employer we actually found so the row justifies its own rejection.
      return empResult.company || empResult.currentCompany
        ? `No - Now at ${empResult.company || empResult.currentCompany}`
        : 'No - Changed Company';
    case EMPLOYMENT_STATUS.UNVERIFIABLE:
      // The gate says which kind of nothing it got: a profile that does not exist is a different
      // finding from one it could not read, and the buyer can act on the difference.
      return (empResult.profileNotFound || empResult.notFound)
        ? 'Unverifiable - LinkedIn profile not found'
        : 'Unverifiable - no LinkedIn match';
    default:
      return 'Not Checked';
  }
}

// Two sources can speak to the same contact, and until now only one of them was ever heard.
//
// The backend's employment gate checks people at search time and writes the verdict onto the
// contact (`employmentCheck`). The export then ran its own live re-check and built the column
// purely from that — so the gate's conclusion was computed, shipped, and discarded. A contact
// the gate had already caught could still be exported, labelled `No - Now at X` instead of
// being left out.
//
// The precedence rule is deliberately asymmetric:
//
//   • REJECTED from EITHER source wins. A readable profile naming a different employer is
//     positive evidence, and it does not expire because a later call timed out. Someone the
//     gate contradicted at search time is not un-contradicted by a failed re-check, and someone
//     the gate merely could not reach is not exonerated when a live call now contradicts them.
//   • Otherwise the fresher observation wins, because the only thing the live check adds is a
//     job change that happened after the search.
//
// `rejected: true` means the row must not be exported at all — labelling it was the bug.
export function resolveEmployment(contact, liveResult) {
  const stamp = readStamp(contact);
  const rejected =
    statusOf(liveResult) === EMPLOYMENT_STATUS.REJECTED ||
    statusOf(stamp)      === EMPLOYMENT_STATUS.REJECTED;

  return { verdict: liveResult ?? stamp ?? null, rejected };
}

// Chat history persists in localStorage and Supabase, so contacts saved before the gate existed
// will be re-exported with no stamp at all. They must degrade to the old behaviour, not crash.
function readStamp(contact) {
  const check = contact?.employmentCheck;
  if (check?.status) return check;
  // Legacy: web-fallback rows carried a bare boolean before `employmentCheck` existed. Only
  // `true` is meaningful — `null` there meant "LinkedIn unreachable", never "they left".
  if (contact?.employmentConfirmed === true) {
    return { status: EMPLOYMENT_STATUS.VERIFIED, currentCompany: '', currentTitle: '' };
  }
  return null;
}

function statusOf(result) {
  if (result == null) return null;
  return result.status
    || (result.confirmed ? EMPLOYMENT_STATUS.VERIFIED : EMPLOYMENT_STATUS.UNVERIFIABLE);
}

// The Title column, reconciled the same way the verdict is.
//
// A row can be right about the person and the employer and still be wrong about the job: Jessica
// Kudlacek never left Everlaw, but the SDR title the vendor recorded belongs to a role she has
// since moved out of. The backend gate now rewrites `contact.title` from LinkedIn at search
// time; this is the export's turn to do the same, because the live re-check running a few lines
// above can be newer than the search - it is the only thing here that can see a job change that
// happened after the row was pulled.
//
// Gated on VERIFIED for the same reason the backend gates it: the title we were handed belongs
// to whichever position LinkedIn listed, and only a confirmed employer tells us that position is
// the one this row is about. Anything less and we keep what we had.
export function currentTitleFor(empResult, fallback = '') {
  if (statusOf(empResult) !== EMPLOYMENT_STATUS.VERIFIED) return fallback;
  return empResult?.currentTitle || fallback;
}

// Should we show this contact's LinkedIn URL at all?
//
// "Unverifiable - no LinkedIn match" means we fetched that profile and it gave us nothing back,
// which in practice means the URL is dead. Handing the user a link that 404s costs more trust
// than showing no link: they click it, land on a missing page, and reasonably start doubting
// the rest of the row.
//
// Deliberately keyed on the UNVERIFIABLE verdict and nothing else. A null `empResult` means
// "we never checked" - which is what happens when the verify call throws, LinkdAPI returns
// non-OK, or the contact fell outside the credit budget. Suppressing on "anything but verified"
// would blank every URL during a vendor outage, turning a temporary blip into what looks like
// missing data.
export function shouldShowLinkedinUrl(empResult) {
  if (empResult == null) return true;          // never checked - no reason to doubt it
  const status = empResult.status
    || (empResult.confirmed ? EMPLOYMENT_STATUS.VERIFIED : EMPLOYMENT_STATUS.UNVERIFIABLE);
  return status !== EMPLOYMENT_STATUS.UNVERIFIABLE;
}

// The URL a row may be exported or shown with, or ''.
//
// One rule for the CSV, the export preview and the batch table, which used to disagree: the file
// applied shouldShowLinkedinUrl and the preview beside it printed the raw URL, so a link the
// download would blank was still on screen to be copied. Reconciled the same way the verdict is
// (resolveEmployment: a live re-check first, the search-time stamp otherwise). A live re-check
// that found the real profile by name supplies a corrected URL, which is fine to show - that one
// we resolved.
export function deliverableLinkedinUrl(contact, live = null) {
  if (live?.correctedLinkedinUrl) return live.correctedLinkedinUrl;
  const { verdict } = resolveEmployment(contact, live);
  return shouldShowLinkedinUrl(verdict) ? (contact?.linkedinUrl || '') : '';
}

// Where the PERSON is, from their LinkedIn profile or the company roster, when a check reached
// them - never the company's HQ, which is what City/State carry. One rule for the export preview
// and the file it writes, which used to keep separate column lists: the preview gained a Location
// column and the file did not, so every download ended in a column headed "undefined".
export function personLocationOf(contact, live = null) {
  return live?.currentLocation || contact?.location || contact?.employmentCheck?.currentLocation || '';
}

// Short badge text for the contact row, or null when there is nothing worth showing.
export function employmentBadge(empResult) {
  if (empResult == null) return null;
  const status = empResult.status
    || (empResult.confirmed ? EMPLOYMENT_STATUS.VERIFIED : EMPLOYMENT_STATUS.UNVERIFIABLE);
  switch (status) {
    case EMPLOYMENT_STATUS.VERIFIED:     return 'Verified';
    case EMPLOYMENT_STATUS.REJECTED:     return 'Changed company';
    case EMPLOYMENT_STATUS.UNVERIFIABLE: return 'Unverifiable';
    default:                             return null;
  }
}
